// client/src/pages/admin/HelpRequests.jsx
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  Assignment,
  Search,
  FilterList,
  Refresh,
  Visibility,
  Comment,
  CheckCircle,
  Chat as ChatIcon,
  Send,
  Close,
  HelpOutline
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';

export default function HelpRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    urgency: 'all'
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [responseText, setResponseText] = useState('');

// CHAT-RELATED STATE - UPDATED FOR CONVERSATION HISTORY
const [chatDialogOpen, setChatDialogOpen] = useState(false);
const [selectedConversation, setSelectedConversation] = useState(null);
const [conversations, setConversations] = useState([]);
const [newChatMessage, setNewChatMessage] = useState('');
const [chatLoading, setChatLoading] = useState(false);
const [sendingMessage, setSendingMessage] = useState(false);
const chatPollingRef = useRef(null);

// Fetch all chat conversations for admin - NEW FUNCTION

const fetchAdminConversations = async (silent = false) => {
  try {
    if (!silent) setChatLoading(true);
    
    const token = localStorage.getItem('adminToken');
    const response = await fetch('https://propertyearn.net/api/api/admin/help-requests', {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) throw new Error('Failed to fetch conversations');
    
    const data = await response.json();
    
    // Filter only chat conversations and format them
    const chatConversations = data.requests
      .filter(request => request.is_chat_conversation)
      .map(request => ({
        conversation_id: request.id,
        user_id: request.user_id,
        user_name: request.user_name,
        user_email: request.user_email,
        initial_message: request.message,
        status: request.status,
        is_resolved: request.status === 'resolved',
        last_activity: request.updated_at,
        message_count: 0,
        unread_count: 0
      }));
    
    console.log('📋 Admin conversations:', chatConversations.length, 'found');
    
    // Only update conversations state if we're not in silent mode
    if (!silent) {
      setConversations(chatConversations);
    }
    
    return chatConversations;
  } catch (err) {
    console.error('Error fetching admin conversations:', err);
    if (!silent) {
      setError('Failed to load conversations');
    }
    return [];
  } finally {
    if (!silent) setChatLoading(false);
  }
};

// Fetch messages for a specific conversation - UPDATED
const fetchConversationMessages = async (conversationId) => {
  try {
    console.log('🔄 Fetching messages for conversation:', conversationId);
    
    const token = localStorage.getItem('adminToken');
    const response = await fetch(
      `https://propertyearn.net/api/api/admin/chat/conversations/${conversationId}/messages`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch messages: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ Fetched messages for conversation:', conversationId, 'Count:', data.messages?.length);
    
    // 🚨 CRITICAL FIX: Only update if this is still the selected conversation
    setSelectedConversation(prev => {
      if (prev && prev.conversation_id === conversationId) {
        return {
          ...prev,
          messages: data.messages || [],
          user_name: data.conversation?.user_name || prev.user_name,
          user_email: data.conversation?.user_email || prev.user_email
        };
      }
      // If conversation changed, don't update
      console.log('⚠️ Conversation changed during fetch, ignoring response');
      return prev;
    });
    
    return data.messages || [];
  } catch (err) {
    console.error('❌ Error fetching conversation messages:', err);
    setError('Failed to load messages: ' + err.message);
    return [];
  }
};

// Send message to conversation - UPDATED

const sendChatMessage = async () => {
  if (!newChatMessage.trim() || !selectedConversation) return;

  try {
    setSendingMessage(true);
    const token = localStorage.getItem('adminToken');
    const conversationId = selectedConversation.conversation_id;
    
    console.log('📤 Sending message to conversation:', conversationId);
    
    const response = await fetch(
      `https://propertyearn.net/api/api/admin/chat/conversations/${conversationId}/respond`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newChatMessage.trim()
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send message');
    }
    
    const result = await response.json();
    console.log('✅ Message sent successfully to conversation:', conversationId);
    
    // Clear input
    setNewMessage('');
    
    // 🚨 CRITICAL: Only refresh messages for the current conversation
    if (selectedConversation?.conversation_id === conversationId) {
      await fetchConversationMessages(conversationId);
    }
    
  } catch (err) {
    setError('Failed to send message: ' + err.message);
    console.error('Send message error:', err);
  } finally {
    setSendingMessage(false);
  }
};

// Open chat with a conversation - UPDATED
const handleOpenChat = async (user, requestId = null) => {
  try {
    console.log('Opening chat for user:', user);
    
    // First, fetch all conversations to find the right one
    const allConversations = await fetchAdminConversations();
    
    // Find existing conversation for this user, or use provided requestId
    let conversation = null;
    if (requestId) {
      conversation = allConversations.find(conv => conv.conversation_id === requestId);
    } else {
      conversation = allConversations.find(conv => conv.user_id === user.id);
    }
    
    if (!conversation) {
      setError('No active conversation found for this user');
      return;
    }
    
    console.log('Found conversation:', conversation);
    
    // Set the selected conversation
    setSelectedConversation({
      ...conversation,
      messages: [] // Clear while loading
    });
    
    setChatDialogOpen(true);
    
    // Fetch messages for this conversation
    await fetchConversationMessages(conversation.conversation_id);
    
  } catch (err) {
    setError('Failed to open chat: ' + err.message);
    console.error('Open chat error:', err);
  }
};

// Close chat dialog
const handleCloseChat = () => {
  console.log('🔒 Closing chat dialog');
  
  // Clear polling first
  if (chatPollingRef.current) {
    clearInterval(chatPollingRef.current);
    chatPollingRef.current = null;
  }
  
  // Reset all chat-related state
  setChatDialogOpen(false);
  setSelectedConversation(null);
  setNewChatMessage('');
  setChatLoading(false);
};

// Select conversation from list - NEW FUNCTION
const handleSelectConversation = async (conversation) => {
  console.log('🔍 Selecting conversation:', conversation.conversation_id, 'for user:', conversation.user_name);
  
  // Clear current messages first to prevent mixing
  setSelectedConversation({
    ...conversation,
    messages: [] // Clear messages while loading new ones
  });
  
  // Fetch messages specifically for this conversation
  await fetchConversationMessages(conversation.conversation_id);
};

// Resolve conversation - NEW FUNCTION
const handleResolveConversation = async (conversationId) => {
  try {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(
      `https://propertyearn.net/api/api/admin/chat/conversations/${conversationId}/resolve`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to resolve conversation');
    }
    
    setSuccess('Conversation marked as resolved');
    
    // Refresh conversations and messages
    await fetchAdminConversations();
    if (selectedConversation?.conversation_id === conversationId) {
      await fetchConversationMessages(conversationId);
    }
    
  } catch (err) {
    setError('Failed to resolve conversation: ' + err.message);
  }
};

// POLLING FOR REAL-TIME UPDATES - UPDATED

useEffect(() => {
  if (chatDialogOpen && selectedConversation) {
    console.log('🔄 Starting polling for conversation:', selectedConversation.conversation_id);
    
    // Set up polling every 5 seconds for the CURRENT conversation only
    chatPollingRef.current = setInterval(() => {
      console.log('🔄 Polling for conversation:', selectedConversation.conversation_id);
      
      // Only refresh messages for the currently selected conversation
      if (selectedConversation?.conversation_id) {
        fetchConversationMessages(selectedConversation.conversation_id);
      }
      
      fetchAdminConversations().then(convs => {
       // Only update if we need to show status changes, but don't change selected conversation
      });
      
    }, 5000);
    
    // Cleanup interval when dialog closes or conversation changes
    return () => {
      console.log('🛑 Stopping polling for conversation');
      if (chatPollingRef.current) {
        clearInterval(chatPollingRef.current);
        chatPollingRef.current = null;
      }
    };
  }
}, [chatDialogOpen, selectedConversation?.conversation_id]);

  // Fetch help requests from API
  const fetchHelpRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch('https://propertyearn.net/api/api/admin/help-requests', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch help requests');
      
      const data = await response.json();
        // ADD DEBUG LOGGING
          console.log('Fetched help requests:', data.requests);
          if (data.requests.length > 0) {
            console.log('First request user data:', {
              user_id: data.requests[0].user_id,
              user_name: data.requests[0].user_name,
              user_email: data.requests[0].user_email
            });
          }
      setRequests(data.requests);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHelpRequests();
  }, []);

  // Filter requests based on search and filters
  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.message.toLowerCase().includes(filters.search.toLowerCase()) ||
                         request.user_name.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = filters.status === 'all' || request.status === filters.status;
    const matchesUrgency = filters.urgency === 'all' || request.urgency === filters.urgency;
    
    return matchesSearch && matchesStatus && matchesUrgency;
  });

  // Handle assigning a request to current admin
  const handleAssignRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`https://propertyearn.net/api/api/admin/help-requests/${requestId}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to assign request');
      
      // Refresh the list
      fetchHelpRequests();
      setSuccess('Request assigned successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle submitting a response (without auto-resolving)
const handleSubmitResponse = async () => {
  try {
    const token = localStorage.getItem('adminToken');
    
    // First, try to assign the request if not already assigned
    if (!selectedRequest.assigned_to || selectedRequest.assigned_admin_name === null) {
      console.log('Auto-assigning request before responding...');
      const assignResponse = await fetch(`https://propertyearn.net/api/api/admin/help-requests/${selectedRequest.id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!assignResponse.ok) {
        throw new Error('Failed to assign request');
      }
      console.log('Request assigned successfully');
    }
    
    // Now submit the response
    const response = await fetch(`https://propertyearn.net/api/api/admin/help-requests/${selectedRequest.id}/respond`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ response: responseText })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit response');
    }
    
    // Close dialog and refresh
    setResponseDialogOpen(false);
    setResponseText('');
    setSelectedRequest(null);
    fetchHelpRequests();
    setSuccess('Response submitted successfully');
  } catch (err) {
    setError(err.message);
    console.error('Response error:', err);
  }
};

  // Handle manual resolution
  const handleResolveRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`https://propertyearn.net/api/api/admin/help-requests/${requestId}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to resolve request');
      
      // Refresh the list
      fetchHelpRequests();
      setSuccess('Request marked as resolved successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  // Get color for status chip
  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'primary';
      case 'in_progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  // Get color for urgency chip
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

// CHAT-RELATED FUNCTIONS
// Fetch chat messages for a specific user - IMPROVED VERSION
const fetchUserChatMessages = async (userId, silent = false) => {
  if (!userId) {
    if (!silent) setError('User ID is required to fetch chat messages');
    return;
  }

  try {
    if (!silent) setChatLoading(true);
    const token = localStorage.getItem('adminToken');
    
    // 🚨 FIX: First find the conversation ID for this user from help_requests
    const response = await fetch('https://propertyearn.net/api/api/admin/help-requests', {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch help requests');
    }
    
    const data = await response.json();
    
    // Find the conversation for this user
    const userConversation = data.requests.find(request => 
      request.user_id === userId && request.is_chat_conversation
    );
    
    if (!userConversation) {
      console.log('No conversation found for user:', userId);
      setChatMessages([]);
      return;
    }
    
    const conversationId = userConversation.id;
    console.log('Found conversation ID:', conversationId, 'for user:', userId);
    
    // 🚨 FIX: Now use the correct endpoint to get messages
    const messagesResponse = await fetch(
      `https://propertyearn.net/api/api/admin/chat/conversations/${conversationId}/messages`, 
      {
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );

    if (!messagesResponse.ok) {
      throw new Error(`Failed to fetch chat messages: ${messagesResponse.status}`);
    }
    
    const messagesData = await messagesResponse.json();
    console.log('Fetched messages:', messagesData.messages);
    setChatMessages(messagesData.messages || []);
    
  } catch (err) {
    if (!silent) {
      setError('Failed to load chat messages: ' + err.message);
      console.error('Chat fetch error:', err);
    }
  } finally {
    if (!silent) setChatLoading(false);
  }
};


// POLLING FOR REAL-TIME UPDATES
useEffect(() => {
  if (chatDialogOpen && selectedConversation) {
    // Fetch immediately when dialog opens
    fetchUserChatMessages(selectedConversation.id);
    
    // Set up polling every 5 seconds for new messages
    chatPollingRef.current = setInterval(() => {
      fetchUserChatMessages(selectedConversation.id, true); // Silent fetch
    }, 5000);
    
    // Cleanup interval when dialog closes or user changes
    return () => {
      if (chatPollingRef.current) {
        clearInterval(chatPollingRef.current);
        chatPollingRef.current = null;
      }
    };
  }
}, [chatDialogOpen, selectedConversation]);


// ADD this helper function:
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

  if (loading) {
    return (
      <AdminLayout pageTitle="Help Requests">
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Help Requests">
      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search requests..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            size="small"
          />
          
          <TextField
            select
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>
          
          <TextField
            select
            label="Urgency"
            value={filters.urgency}
            onChange={(e) => setFilters({ ...filters, urgency: e.target.value })}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All Urgencies</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </TextField>
          
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilters({ search: '', status: 'all', urgency: 'all' })}
          >
            Clear Filters
          </Button>
          
          <Box flexGrow={1} />
          
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={fetchHelpRequests}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Requests Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Urgency</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No help requests found
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>#{request.id}</TableCell>
                  <TableCell>{request.user_name || 'Unknown User'}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography noWrap title={request.message}>
                      {request.message.length > 100 
                        ? `${request.message.substring(0, 100)}...` 
                        : request.message}
                    </Typography>
                  </TableCell>
                  <TableCell>{request.category}</TableCell>
                  <TableCell>
                    <Chip 
                      label={request.urgency} 
                      size="small" 
                      color={getUrgencyColor(request.urgency)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={request.status} 
                      size="small" 
                      color={getStatusColor(request.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(request.created_at).toLocaleDateString()}
                  </TableCell>

                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => setSelectedRequest(request)}
                        title="View details"
                      >
                        <Visibility />
                      </IconButton>
                      
                      {/* Show assignment status */}
                      {(!request.assigned_to || request.assigned_to === null) && (
                        <IconButton 
                          size="small" 
                          onClick={() => handleAssignRequest(request.id)}
                          title="Assign to me"
                          color="primary"
                        >
                          <Assignment />
                        </IconButton>
                      )}
                      
                      {/* Show respond button if assigned to current admin or unassigned */}
                      {(request.status === 'in_progress' || !request.assigned_to) && (
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setSelectedRequest(request);
                            setResponseDialogOpen(true);
                          }}
                          title="Respond"
                          color="secondary"
                        >
                          <Comment />
                        </IconButton>
                      )}
                      
                      {/* Show resolve button if assigned to current admin */}
                      {request.status === 'in_progress' && request.assigned_to && (
                        <IconButton 
                          size="small" 
                          onClick={() => handleResolveRequest(request.id)}
                          title="Mark as resolved"
                          color="success"
                        >
                          <CheckCircle />
                        </IconButton>
                      )}
                      
                      {/* Debug button - remove in production */}
                      <IconButton 
                        size="small" 
                        onClick={async () => {
                          const token = localStorage.getItem('adminToken');
                          const response = await fetch(`https://propertyearn.net/api/api/admin/debug/request/${request.id}`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            const data = await response.json();
                            console.log('Debug info:', data);
                          }
                        }}
                        title="Debug info"
                        color="default"
                      >
                        <HelpOutline />
                      </IconButton>

                    {/* Chat Button */}         
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenChat(
                          {
                            id: request.user_id,
                            name: request.user_name,
                            email: request.user_email
                          },
                          request.id // Pass the request ID as conversation ID
                        )}
                        title="Open Chat"
                        color="info"
                        disabled={!request.is_chat_conversation}
                      >
                        <ChatIcon />
                      </IconButton>
                    </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Request Detail Dialog */}
      <Dialog 
        open={!!selectedRequest} 
        onClose={() => setSelectedRequest(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedRequest && (
          <>
            <DialogTitle>
              Help Request #{selectedRequest.id}
            </DialogTitle>
            <DialogContent>
              <Box mb={2}>
                <Typography variant="subtitle2">User:</Typography>
                <Typography>{selectedRequest.user_name || 'Unknown User'}</Typography>
              </Box>
              
              <Box mb={2}>
                <Typography variant="subtitle2">Category:</Typography>
                <Typography>{selectedRequest.category}</Typography>
              </Box>
              
              <Box mb={2}>
                <Typography variant="subtitle2">Urgency:</Typography>
                <Chip 
                  label={selectedRequest.urgency} 
                  color={getUrgencyColor(selectedRequest.urgency)}
                />
              </Box>
              
              <Box mb={2}>
                <Typography variant="subtitle2">Status:</Typography>
                <Chip 
                  label={selectedRequest.status} 
                  color={getStatusColor(selectedRequest.status)}
                />
              </Box>
              
              <Box mb={2}>
                <Typography variant="subtitle2">Message:</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography>{selectedRequest.message}</Typography>
                </Paper>
              </Box>
              
              {selectedRequest.response && (
                <Box mb={2}>
                  <Typography variant="subtitle2">Response:</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.light' }}>
                    <Typography>{selectedRequest.response}</Typography>
                  </Paper>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedRequest(null)}>Close</Button>
              {selectedRequest.status === 'new' && (
                <Button 
                  variant="contained" 
                  onClick={() => handleAssignRequest(selectedRequest.id)}
                >
                  Assign to Me
                </Button>
              )}
              {selectedRequest.status === 'in_progress' && (
                <>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setSelectedRequest(null);
                      handleResolveRequest(selectedRequest.id);
                    }}
                  >
                    Mark as Resolved
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={() => setResponseDialogOpen(true)}
                  >
                    Respond
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Response Dialog */}
      <Dialog 
        open={responseDialogOpen} 
        onClose={() => setResponseDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Respond to Help Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Responding to this request will not automatically mark it as resolved.
            You can mark it as resolved separately after the issue is fully addressed.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder="Type your response here..."
            sx={{ mt: 1 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitResponse}
            disabled={!responseText.trim()}
          >
            Submit Response
          </Button>
        </DialogActions>
      </Dialog>

        {/* Chat Dialog - UPDATED WITH CONVERSATION HISTORY */}
        <Dialog 
          open={chatDialogOpen} 
          onClose={handleCloseChat}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              height: '70vh',
              maxHeight: '600px'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            backgroundColor: 'primary.main',
            color: 'white'
          }}>
            <Box>
              <Typography variant="h6">Support Chat</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Admin Chat Interface
              </Typography>
            </Box>
            <IconButton onClick={handleCloseChat} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, display: 'flex' }}>
            {/* Conversations List - SIMILAR TO USER SIDE */}
            <Box sx={{ 
              width: 300, 
              borderRight: '1px solid', 
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Conversations
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {conversations.length} total
                </Typography>
              </Box>
              
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {conversations.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No conversations yet
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {conversations.map((conversation) => (
                      <ListItem
                        key={conversation.conversation_id}
                        button
                        selected={selectedConversation?.conversation_id === conversation.conversation_id}
                        onClick={() => handleSelectConversation(conversation)}
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                          }
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ 
                            bgcolor: conversation.is_resolved ? 'success.main' : 'primary.main',
                            width: 32,
                            height: 32
                          }}>
                            {conversation.user_name?.charAt(0) || 'U'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" noWrap>
                              {conversation.user_name || 'Unknown User'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" noWrap>
                              {conversation.is_resolved ? 'Resolved' : 'Active'} • 
                              {formatTime(conversation.last_activity)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            {/* Chat Area */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h6">
                          {selectedConversation.user_name || 'Unknown User'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedConversation.user_email}
                          {selectedConversation.conversation_id && ` • Conversation #${selectedConversation.conversation_id}`}
                        </Typography>
                      </Box>
                      <Box>
                        <Chip 
                          label={selectedConversation.is_resolved ? 'Resolved' : 'Active'} 
                          color={selectedConversation.is_resolved ? 'success' : 'primary'}
                          size="small"
                        />
                        {!selectedConversation.is_resolved && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            sx={{ ml: 1 }}
                            onClick={() => handleResolveConversation(selectedConversation.conversation_id)}
                          >
                            Resolve
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Messages Area */}
                  <Box sx={{ 
                    flexGrow: 1, 
                    p: 2, 
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}>
                    {selectedConversation.messages && selectedConversation.messages.length > 0 ? (
                      selectedConversation.messages.map((message) => (
                        <Box
                          key={message.id}
                          sx={{
                            display: 'flex',
                            justifyContent: message.sender_type === 'admin' ? 'flex-end' : 'flex-start',
                            mb: 1
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start',
                            maxWidth: '70%',
                            flexDirection: message.sender_type === 'admin' ? 'row-reverse' : 'row'
                          }}>
                            <Avatar sx={{ 
                              width: 32, 
                              height: 32, 
                              mx: 1,
                              bgcolor: message.sender_type === 'admin' ? 'primary.main' : 'secondary.main',
                              fontSize: '0.75rem'
                            }}>
                              {message.sender_type === 'admin' ? 'A' : 'U'}
                            </Avatar>
                            <Paper
                              sx={{
                                p: 1.5,
                                backgroundColor: message.sender_type === 'admin' ? 'primary.main' : 'grey.100',
                                color: message.sender_type === 'admin' ? 'white' : 'text.primary',
                                borderRadius: 2,
                                maxWidth: '100%'
                              }}
                            >
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {message.message}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  display: 'block',
                                  mt: 0.5,
                                  opacity: 0.7,
                                  color: message.sender_type === 'admin' ? 'white' : 'text.secondary'
                                }}
                              >
                                {new Date(message.timestamp).toLocaleString()}
                                {message.sender_name && ` • ${message.sender_name}`}
                              </Typography>
                            </Paper>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        flexDirection: 'column',
                        p: 3
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          No messages yet. Start the conversation!
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider />
                  
                  {/* Input Area */}
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type your message..."
                        value={newChatMessage}
                        onChange={(e) => setNewChatMessage(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendChatMessage();
                          }
                        }}
                        disabled={sendingMessage || selectedConversation.is_resolved}
                        size="small"
                        multiline
                        maxRows={3}
                      />
                      <IconButton 
                        onClick={sendChatMessage} 
                        disabled={!newChatMessage.trim() || sendingMessage || selectedConversation.is_resolved}
                        color="primary"
                        sx={{ alignSelf: 'flex-end' }}
                      >
                        {sendingMessage ? <CircularProgress size={24} /> : <Send />}
                      </IconButton>
                    </Box>
                    {selectedConversation.is_resolved && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        This conversation has been resolved. No further messages can be sent.
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                /* No conversation selected */
                <Box sx={{ 
                  flexGrow: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  p: 3
                }}>
                  <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Select a conversation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Choose a conversation from the list to view messages
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
    </AdminLayout>
  );
}